/**
 * batch-56 notificatie-override (FO-416) - ontvangers per meldingssoort.
 *
 * Gedeeld contract tussen de API (`routes/notificatie-ontvangers.ts` +
 * `lib/notificatie-events/ontvanger-override.ts`) en het beheerscherm
 * (`/instellingen/notificaties/ontvangers`). Eén schema, beide kanten - zodat
 * het formulier nooit iets kan sturen dat de API weigert (CLAUDE.md
 * "Validatie & foutmeldingen").
 */

import { z } from 'zod'
import {
  NOTIFICATIE_EVENT_TYPE_KEY,
  type NotificatieAfleveringStatus,
  type NotificatieEventTypeKey,
  type NotificatieKanaalType,
} from './notificaties'
// batch-67 meldingen-ontvangers (FO-500) - het herleidbaarheidsspoor leeft in een
// eigen bladmodule (ook gelezen door het lijst-DTO in notificaties.ts).
import {
  NOTIFICATIE_LEVERPADEN,
  NOTIFICATIE_ONTVANGER_BRONNEN,
  type NotificatieOntvangerBron,
  type NotificatieOntvangerBronDTO,
  type NotificatieOntvangerVia,
} from './notificatie-bron'
import type { InboxFunctie } from './postvak-in'

// ─── Soort-sleutel ───────────────────────────────────────────────────────────
//
// Een "meldingssoort" is óf een notificatie-event (`eventKey`) óf een
// signalering-regel zonder gekoppeld event (`ruleCode`). Een regel MÉT
// `eventTypeKey` deelt zijn soort met dat event en wordt dus op de event-sleutel
// geconfigureerd - spec §6.2 ("het scherm toont ze als één soort"), zodat er
// geen twee configuraties voor dezelfde melding kunnen ontstaan.

export type NotificatieSoortBron = 'EVENT' | 'RULE'

export interface NotificatieSoortSleutel {
  bron: NotificatieSoortBron
  /** Gevuld bij bron EVENT. */
  eventKey: NotificatieEventTypeKey | null
  /** Gevuld bij bron RULE (regel zonder gekoppeld event). */
  ruleCode: string | null
}

/** Stabiele string-id van een soort, voor React-keys en URL-segmenten. */
export function soortId(s: NotificatieSoortSleutel): string {
  return s.bron === 'EVENT' ? `event:${s.eventKey ?? ''}` : `rule:${s.ruleCode ?? ''}`
}

// ─── Categorieën (spec §3.1 "gegroepeerd per categorie") ─────────────────────

export const NOTIFICATIE_SOORT_CATEGORIEEN = [
  'FINANCIEEL',
  'HR',
  'PLANNING',
  'MATERIEEL',
  'KLANT',
  'VEILIGHEID',
  'SYSTEEM',
  'OVERIG',
] as const
export type NotificatieSoortCategorie = (typeof NOTIFICATIE_SOORT_CATEGORIEEN)[number]

export const NOTIFICATIE_SOORT_CATEGORIE_LABELS: Record<NotificatieSoortCategorie, string> = {
  FINANCIEEL: 'Financieel',
  HR: 'HR en medewerkers',
  PLANNING: 'Planning en uitvoering',
  MATERIEEL: 'Materieel en voorraad',
  KLANT: 'Klant en acquisitie',
  VEILIGHEID: 'Veiligheid en compliance',
  SYSTEEM: 'Systeem',
  OVERIG: 'Overig',
}

/**
 * Doelgroep-resolvers die per definitie de administratie/facturatie aanspreken.
 * Een melding met zo'n doelgroep is een financieel signaal, ongeacht in welke
 * module hij geregistreerd staat - dat is precies de groep die de PO op een
 * enkele persoon wil kunnen zetten.
 */
const FINANCIELE_RESOLVERS: readonly string[] = ['administratie', 'facturen.approvers']

const MODULE_CATEGORIE: Record<string, NotificatieSoortCategorie> = {
  facturen: 'FINANCIEEL',
  inkoop: 'FINANCIEEL',
  'inkoop-budget': 'FINANCIEEL',
  declaraties: 'FINANCIEEL',
  salaris: 'FINANCIEEL',
  keuzebudget: 'FINANCIEEL',
  abonnementen: 'FINANCIEEL',
  hrm: 'HR',
  medewerkers: 'HR',
  verlof: 'HR',
  verzuim: 'HR',
  onboarding: 'HR',
  'offboarding-workflow': 'HR',
  // batch-71 offboarding-inname (FO-535 §7)
  'offboarding-salaris': 'HR',
  'offboarding-inname-conflict': 'HR',
  recruitment: 'HR',
  opleidingen: 'HR',
  gesprekken: 'HR',
  'hr-gesprekken': 'HR',
  dossier: 'HR',
  vertrouwenspersoon: 'HR',
  werkgeversverklaring: 'HR',
  'informatie-verzoeken': 'HR',
  // batch-66 contract-mijlpalen (FO-496) - de mijlpaal-events van het contract.
  'contract-mijlpalen': 'HR',
  mutatieverzoeken: 'HR',
  ideeenbox: 'HR',
  planning: 'PLANNING',
  werkbonnen: 'PLANNING',
  uren: 'PLANNING',
  weekstaten: 'PLANNING',
  reisuren: 'PLANNING',
  reisafstanden: 'PLANNING',
  consignatie: 'PLANNING',
  'operationele-taken': 'PLANNING',
  materieel: 'MATERIEEL',
  voorraad: 'MATERIEEL',
  uitrusting: 'MATERIEEL',
  karren: 'MATERIEEL',
  klantenportaal: 'KLANT',
  // batch-60 klantportaal-zichtbaar-verzoeken (FO-439b K4) — de per-soort
  // klantverzoek-events horen bij de klant-kant, niet onder OVERIG.
  klantverzoeken: 'KLANT',
  acquisitie: 'KLANT',
  contactverzoeken: 'KLANT',
  crm: 'KLANT',
  // batch-59 onboarding-review (FO-437) — de relatie-onboarding-review-events.
  // Zonder deze regel vallen ze in het ontvangers-beheerscherm onder OVERIG en
  // is de doelgroep alleen met zoeken te vinden; ze horen bij de relatie-kant.
  'relatie-onboarding': 'KLANT',
  veiligheid: 'VEILIGHEID',
  ongevallen: 'VEILIGHEID',
  mail: 'SYSTEEM',
  documenten: 'SYSTEEM',
  systeem: 'SYSTEEM',
}

const RULE_CATEGORIE: Record<string, NotificatieSoortCategorie> = {
  PLANNING: 'PLANNING',
  COMPLIANCE: 'VEILIGHEID',
  HR: 'HR',
  OPERATIONEEL: 'PLANNING',
  SYSTEEM: 'SYSTEEM',
  KWALITEIT: 'VEILIGHEID',
}

/**
 * De categorie waaronder een meldingssoort in het beheerscherm valt.
 *
 * De doelgroep-resolver wint van de module/regel-categorie: `administratie` is
 * de permissiegroep FACTUREN.canView, dus een melding die daarheen gaat is een
 * financieel signaal - ook als hij (zoals `FACTUUR_TE_LANG_OPEN`) in de
 * regel-engine als OPERATIONEEL geregistreerd staat.
 */
export function notificatieSoortCategorie(input: {
  /** `NotificatieEvent.module` (event) - leeg bij een regel. */
  module?: string | null
  /** `recipientResolver` van het event of de regel. */
  resolver?: string | null
  /** `NotificationRule.category` - leeg bij een event. */
  ruleCategory?: string | null
}): NotificatieSoortCategorie {
  if (input.resolver && FINANCIELE_RESOLVERS.includes(input.resolver)) return 'FINANCIEEL'
  if (input.module) {
    const uitModule = MODULE_CATEGORIE[input.module]
    if (uitModule) return uitModule
  }
  if (input.ruleCategory) {
    const uitRegel = RULE_CATEGORIE[input.ruleCategory]
    if (uitRegel) return uitRegel
  }
  return 'OVERIG'
}

/**
 * batch-56 notificatie-override (FO-416) - meldingssoorten die NIET langs het
 * interceptiepunt lopen en dus (nog) niet instelbaar zijn.
 *
 * De override grijpt in op de DOELGROEP-laag: hij vervangt de uitkomst van een
 * recipient-resolver vlak voor de aflevering. Deze event-keys worden uitsluitend
 * afgeleverd door code die zelf al een ontvangerslijst heeft samengesteld - een
 * `notify({ recipient: { userId } })`-lus of een directe `createNotificatie`-lus.
 * Daar valt niets meer te vervangen: wegfilteren zou nog kunnen, maar de gekozen
 * ontvanger TOEVOEGEN niet, want de aanroeper itereert nooit over hem.
 *
 * Waarom dit een expliciete lijst is en geen stilte: zonder deze klem zou het
 * beheerscherm de melding als instelbaar tonen, "Nu: {naam}" melden en er in
 * werkelijkheid niets veranderen. Een zichtbare grens met uitleg is beter dan een
 * stille no-op - dezelfde afweging als bij persoonsgebonden en mandatory
 * meldingen.
 *
 * De lijst wordt NIET met de hand bijgehouden: `apps/api/src/lib/__tests__/
 * notificatie-directe-aflevering-drift.test.ts` leidt hem opnieuw af uit de
 * callsites en faalt zodra hij afwijkt. Wordt een fan-out omgezet naar
 * `notify({ recipient: { resolverKey } })` of `triggerNotificatieEvent`, dan
 * verdwijnt de key hier en is de melding vanzelf instelbaar.
 */
export const NIET_INSTELBARE_EVENT_KEYS: readonly string[] = [
  'ABONNEMENT_FACTUUR_AFWIJKING',
  'ABONNEMENT_INDEXERING_SAMENVATTING',
  'ABONNEMENT_VERLOOP',
  // batch-67 campagnes (FO-501) - persoonlijke berichten aan de berijder zelf
  // ("geef je keuze door", de uitkomst van zijn eigen beoordeling): er valt geen
  // doelgroep te vervangen.
  'BIJTELLING_CAMPAGNE_HERINNERING',
  'BIJTELLING_CAMPAGNE_START',
  'BIJTELLING_KEUZE_BEOORDEELD',
  'BULK_WIJZIGING_DIGEST',
  'CAMPAGNE_AFGEWEZEN',
  'CAMPAGNE_BEOORDELING_OPEN',
  'CAMPAGNE_EERSTE_MELDING',
  'CAMPAGNE_GOEDGEKEURD',
  // batch-70 vakantiecampagne (FO-525) - persoonlijk aan de medewerker zelf: "je saldo is
  // aangepast, bekijk je advies opnieuw" en de uitkomst van zijn eigen aanvraag.
  'CAMPAGNE_SALDO_GEWIJZIGD',
  'CAMPAGNE_UITKOMST',
  // batch-70 uitrusting-controle (FO-531) - start en herinnering gaan naar de
  // deelnemer zelf en gaan over zijn eigen controle.
  'CAMPAGNE_UITRUSTING_HERINNERING',
  'CAMPAGNE_UITRUSTING_START',
  'CAMPAGNE_WACHTLIJST',
  'CONTRACT_AANZEGTERMIJN_KRITIEK',
  'CONTRACT_VERLOOP',
  'CONTRACT_VERLOPEN',
  // batch-68 handtekening (FO-345) - "jij bent aan de beurt om te tekenen" en
  // "je handtekening is vervallen" gaan naar de ENE aangewezen ondertekenaar
  // (`notify({ recipient: { userId } })`). Er valt geen doelgroep te vervangen.
  'DOCUMENT_HANDTEKENING_VERVALLEN',
  'DOCUMENT_ONDERTEKENEN_AAN_DE_BEURT',
  // batch-67 campagnes (FO-503) - uitnodiging, herinnering en bevestiging gaan naar
  // de medewerker zelf en gaan over zijn eigen aanmelding.
  'EVENEMENT_BEVESTIGING',
  'EVENEMENT_HERINNERING',
  'EVENEMENT_UITNODIGING',
  'FREQUENT_VERZUIM_GESIGNALEERD',
  'GESPREKSVERSLAG_ACTIEPUNT_ESCALATIE',
  'GESPREKSVERSLAG_ACTIEPUNT_TOEGEWEZEN',
  'GESPREKSVERSLAG_ADDENDUM_TOEGEVOEGD',
  'GESPREKSVERSLAG_GELEZEN',
  'GESPREKSVERSLAG_KLAARGEZET',
  'GESPREKSVERSLAG_MEDEWERKER_HERINNERING',
  'GESPREKSVERSLAG_ONDERTEKENING_VERZOEK',
  'GESPREKSVERSLAG_REACTIE',
  'GESPREKSVERSLAG_STILZWIJGEND_AANGEKONDIGD',
  'GESPREKSVERSLAG_STILZWIJGEND_VASTGESTELD',
  'GESPREKSVERSLAG_TERMIJN_HERINNERING',
  'GESPREKSVERSLAG_TERMIJN_TL_ESCALATIE',
  'GESPREKSVERSLAG_TER_CONTROLE',
  'GESPREKSVERSLAG_TER_CONTROLE_TERUG',
  'GESPREKSVERSLAG_TE_MAKEN',
  'GESPREKSVERSLAG_VERVOLG_PLANNEN',
  'GESPREKSVERSLAG_VOLLEDIG_ONDERTEKEND',
  // batch-67 hr-brieven (FO-506) - "er staat een brief van HR voor je klaar" gaat
  // naar de ENE medewerker om wie het besluit gaat (`notify({ recipient: {
  // employeeId } })`, persoonsgebonden template). Er is geen doelgroep om te
  // vervangen.
  'HR_BESLUIT_BRIEF_VERSTREKT',
  'IMPORT_AFGEROND',
  'INFORMATIE_VERZOEK_ONBEANTWOORD',
  'INTEGRATIE_EXPIRY_NABIJ',
  // batch-71 kennismaking (FO-534) - de houder van het actiepunt is de keuze op de lijst
  // en de samenvatting gaat naar de medewerker zelf: daar valt niets om te sturen.
  'KENNISMAKING_ACTIEPUNT',
  'KENNISMAKING_SAMENVATTING',
  'KLANT_AFWEZIGHEID_REACTIE',
  'KLANT_DOCUMENT_REACTIE',
  'KLANT_MONTEUR_FEEDBACK',
  'KLANT_OFFERTE_BESLUIT',
  'LOONSTROKEN_DOELGROEP_LEEG',
  'LOONSTROKEN_TE_BEOORDELEN',
  'LOONSTROOK_VERZOEK_AFGEROND',
  'LOONSTROOK_VERZOEK_BESLIST',
  'LOONSTROOK_VRAAG',
  'MIDDELEN_RETOURCHECK',
  'ONBOARDING_CONFIGURATIEFOUT',
  'ONBOARDING_HERINNERING',
  // batch-59 voertuig-afspraken (FO-435 stap 4) — het annuleren van een
  // voertuig-afspraak ruimt de interne planregel op en meldt dat aan de ÉNE
  // medewerker die erop stond (`notify({ recipient: { userId } })`). Er valt geen
  // doelgroep te vervangen: de ontvanger is die medewerker, niet een rol. Het
  // beheerscherm toont deze melding daarom eerlijk als niet-instelbaar.
  'PLANNING_GEWIJZIGD',
  'SCHADEMELDING_BLAD2_HERINNERING',
  'SCHADEMELDING_STATUS_UPDATE',
  'TAAK_DAILY_DIGEST',
  // batch-70 wagenpark-week (FO-529 §A2) - de oude en de nieuwe houder van een tankpas horen
  // het zelf (`notify({ recipient: { employeeId } })`, persoonlijk): geen doelgroep te vervangen.
  'TANKPAS_HOUDER_GEWIJZIGD',
  'TARGETING_LEAD_STATUS_CHANGED',
  // batch-70 uitrusting-controle (FO-531) - "aangevuld, teken nu" gaat naar de ene
  // medewerker over zijn eigen koffer; het weeklijstje naar de ene teamleider over
  // zijn eigen team. Er valt geen doelgroep te vervangen.
  'UITRUSTING_AANGEVULD_TEKENEN',
  'UITRUSTING_TEAM_WEEKLIJST',
  'VERLOF_INGETROKKEN',
  'VERLOF_INTREKKING_AFGEWEZEN',
  'VERZUIM_AFGEKEURD',
  'VERZUIM_DOELGROEP_LEEG',
  'VERZUIM_GEACCORDEERD_MEDEWERKER',
  'VERZUIM_GEMELD',
  'VERZUIM_HERSTELD',
  'VERZUIM_WACHT_OP_ACCORDERING',
  // batch-70 wagenpark-week (FO-528 §D8) - de bestuurder zelf wordt gevraagd zijn eigen
  // afwijkingen toe te lichten (persoonlijk, gebundeld per week): geen doelgroep te vervangen.
  'VOERTUIG_TOELICHTING_GEVRAAGD',
  'WERKBON_HEROPEND_VERPLICHT',
]

/** Loopt de aflevering van deze meldingssoort langs het interceptiepunt? */
export function isInstelbareEventKey(key: string): boolean {
  return !NIET_INSTELBARE_EVENT_KEYS.includes(key)
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Eén ontvanger zoals het scherm hem toont. */
export interface NotificatieOntvangerDTO {
  userId: string
  naam: string
  email: string
  rol: string
  /** Actief = niet verwijderd, account actief en dienstverband lopend. */
  actief: boolean
  /**
   * batch-60 (FO-439b K4) — aantal geregistreerde push-devices van deze
   * ontvanger. 0 = "geen push-device geregistreerd": push is dan geen stille
   * no-op maar een zichtbare status in het scherm; in-app en mail blijven.
   */
  pushDevices?: number
}

// batch-67 meldingen-ontvangers (FO-500) - `NotificatieOntvangerBron` is verhuisd
// naar notificatie-bron.ts (met EIGENAAR erbij); hier opnieuw geexporteerd voor
// de bestaande importeurs.
export type { NotificatieOntvangerBron }

export interface NotificatieOntvangerOverrideDTO {
  userIds: string[]
  rollen: string[]
  niemand: boolean
  ingesteldDoorId: string | null
  ingesteldDoorNaam: string | null
  ingesteldOp: string
}

/** Eén rij in het beheerscherm. */
export interface NotificatieSoortDTO extends NotificatieSoortSleutel {
  /** `soortId(...)` - stabiele id voor keys en API-paden. */
  id: string
  naam: string
  omschrijving: string | null
  categorie: NotificatieSoortCategorie
  /** Resolver-key van de standaard-doelgroep. */
  standaardResolver: string
  /** Mens-leesbaar label van de standaard-doelgroep. */
  standaardLabel: string
  /** Persoonsgebonden template (FO-409) - niet configureerbaar. */
  persoonlijk: boolean
  /** Mandatory/compliance - behoudt zijn verplichte doelgroep. */
  mandatory: boolean
  /** Configureerbaar? False bij persoonlijk, mandatory of een niet-geintercepteerd leverpad. */
  configureerbaar: boolean
  /** Reden waarom hij niet configureerbaar is (UI-uitleg). */
  nietConfigureerbaarReden: string | null
  /** Staat het event/de regel überhaupt aan? */
  actief: boolean
  /** De ingestelde override, of null bij standaard-gedrag. */
  override: NotificatieOntvangerOverrideDTO | null
  /** Wie er nú effectief ontvangt. */
  effectieveOntvangers: NotificatieOntvangerDTO[]
  effectieveBron: NotificatieOntvangerBron
  /**
   * Ontvangers uit de override die niet meer actief zijn (uitgediend of
   * geblokkeerd). Niet leeg = waarschuwing in het scherm. Blijft er niemand
   * actiefs over, dan gaat de melding naar de eigenaar met een signaal aan
   * beheer; bij een doelgroep die de betrokkene zelf of een afgeschermde groep is
   * terug naar die eigen doelgroep (batch-67, de ladder in `ontvanger-override.ts`).
   */
  inactieveOntvangers: NotificatieOntvangerDTO[]
  /**
   * batch-67 meldingen-ontvangers (FO-500) - de eigenaar van deze soort: de
   * gerichte terugval-ontvanger zodra de doelgroep niemand specifieks oplevert.
   * `null` = geen eigenaar ingesteld (de drift-test eist er een per event).
   */
  eigenaar: NotificatieEigenaarDTO | null
  /**
   * batch-67 - kan deze soort ooit bij de eigenaar landen? Niet bij een
   * persoonsgebonden melding, niet bij een soort die buiten het interceptiepunt
   * wordt afgeleverd, en niet bij een doelgroep waar leeg gewoon niemand betekent
   * (de betrokkene zelf, een afgeschermde groep). Wel bij een verplichte melding
   * (die houdt haar doelgroep, maar valt bij leeg terug op de eigenaar), bij een
   * escalatie, en bij een signalering die escaleert. Het scherm toont en bewerkt
   * de eigenaar alleen waar dit `true` is.
   */
  eigenaarVanToepassing: boolean
  /**
   * batch-67 - gevuld bij een escalatie-soort (`<ruleCode>:escalatie`): de
   * code van de regel waar deze escalatie bij hoort.
   */
  escalatieVan: string | null
}

// ─── batch-67 meldingen-ontvangers (FO-500) ─────────────────────────────────
//
// Drie aanvullingen op het FO-416-contract:
//   1. een EIGENAAR per meldingssoort (de gerichte terugval, nooit "iedereen");
//   2. een eigen override-sleutel per escalatie (`<ruleCode>:escalatie`), zodat
//      een escalatie nooit meer naar een hele rol gaat zonder instelling;
//   3. de Afleveringen-tab en de simulatie met een echte bron-id.

/**
 * Rollen die als symbolische eigenaar van een meldingssoort kunnen gelden. De
 * seed zet er per event een; het beheerscherm kan er personen bij of voor in
 * de plaats zetten. MONTEUR ontbreekt bewust: een eigenaar is een
 * verantwoordelijke, geen uitvoerder. ACCOUNTANT nooit (FO-404 §7).
 */
export const NOTIFICATIE_EIGENAAR_ROLLEN = [
  'TEAMLEIDER',
  'PLANNER',
  'HR',
  'SALARIS',
  'MANAGER',
  'ADMIN',
] as const
export type NotificatieEigenaarRol = (typeof NOTIFICATIE_EIGENAAR_ROLLEN)[number]

export const NOTIFICATIE_EIGENAAR_ROL_LABELS: Record<NotificatieEigenaarRol, string> = {
  TEAMLEIDER: 'Teamleiders',
  PLANNER: 'Planners',
  HR: 'HR',
  SALARIS: 'Salarisadministratie',
  MANAGER: 'Management',
  ADMIN: 'Beheer (ADMIN)',
}

export function isNotificatieEigenaarRol(waarde: string): waarde is NotificatieEigenaarRol {
  return (NOTIFICATIE_EIGENAAR_ROLLEN as readonly string[]).includes(waarde)
}

/**
 * De STANDAARD-eigenaar per doelgroep-resolver: de rol die verantwoordelijk is
 * voor wat die doelgroep ontvangt. Wint van de module-indeling hieronder, omdat
 * de doelgroep meer zegt dan de module (een melding naar `administratie` is
 * financieel, ook als hij in de module werkbonnen geregistreerd staat - zelfde
 * afweging als `notificatieSoortCategorie`).
 *
 * Alleen DOELGROEP-resolvers staan hier; een gerichte resolver (`employee.self`,
 * `actor.requester`, de monteur van een werkbon) zegt niets over wie de soort
 * beheert en valt door naar de module.
 */
export const EIGENAAR_ROL_PER_RESOLVER: Readonly<Record<string, NotificatieEigenaarRol>> = {
  // HR en medewerkers
  'all-hr': 'HR',
  'all-hr+salaris': 'HR',
  'hr+manager': 'HR',
  'employee.self+hr': 'HR',
  'employee.self+hr+tl': 'HR',
  'leave.approvers': 'HR',
  'leave.teamleiders': 'HR',
  'verjaardag.medewerker': 'HR',
  'verjaardag.teamleider': 'HR',
  'jubileum.hr+teamleider': 'HR',
  'cert.betrokkenen': 'HR',
  'cert.hr+verantwoordelijke': 'HR',
  'onboarding.hr+medewerker': 'HR',
  'kandidaat.recruiter-of-hr': 'HR',
  'kandidaat.beoordelaars': 'HR',
  'kandidaat.besluitnemers': 'HR',
  'kandidaat.info-verzoek': 'HR',
  'hr-gesprek.betrokkenen': 'HR',
  'all-vertrouwenspersonen': 'HR',
  'gesprek.recipient': 'HR',
  'verzuim.contactpersonen': 'HR',
  'contract-mijlpaal.doelgroep': 'HR',
  'contract-mijlpaal.escalatie': 'HR',
  'contract-mijlpaal.hr': 'HR',
  // batch-71 kennismaking (FO-534)
  'kennismaking.verantwoordelijke': 'HR',
  'kennismaking.verantwoordelijke+hr': 'HR',
  'kennismaking.escalatie': 'HR',
  'kennismaking.actiepunt-houder': 'HR',
  'boetes.rijgedrag': 'HR',
  'veiligheid.opvolgers': 'HR',
  'ongevallen.beheerders': 'HR',
  'idee.ontvangers': 'HR',
  'idee.reactie-teamleider': 'HR',
  'idee.organisatie': 'HR',
  'campagne.beheerders': 'HR',
  'evenement.organisatie': 'HR',
  'hr-besluit.controleurs': 'HR',
  // Planning en uitvoering
  planner: 'PLANNER',
  'planner+teamleider': 'PLANNER',
  'planner+hr': 'PLANNER',
  'planning.planners': 'PLANNER',
  'intake.team': 'PLANNER',
  'werkbon.teamleider': 'PLANNER',
  'vervanging.opvolgers': 'PLANNER',
  'reisuren.teamleider': 'PLANNER',
  'klantverzoeken.ontvangers': 'PLANNER',
  'klantverzoeken.escalatie': 'PLANNER',
  'client.responsible': 'PLANNER',
  'relatie-onboarding.beoordelaars': 'PLANNER',
  'reisafwijking.beoordelaars': 'PLANNER',
  'consignatie.ruil-collega+planner': 'PLANNER',
  'consignatie.ruil-aanvrager+planner': 'PLANNER',
  'taak.teamleider': 'PLANNER',
  'taak.escalatie': 'PLANNER',
  'taak.escalatie-aanmaker': 'PLANNER',
  'taak.aanmaker': 'PLANNER',
  // Financieel (administratie)
  administratie: 'SALARIS',
  'facturen.approvers': 'SALARIS',
  // batch-69 werkbon-pauze (integrator-review H1): wie de klantpauze oplost is beheer
  // (relaties bewerken of werkbon-facturatie), niet de salarisadministratie.
  'klantpauze.oplossers': 'ADMIN',
  'inkoop.behandelaar': 'SALARIS',
  'inkoop.goedkeurders': 'SALARIS',
  'inkoopbudget.budgethouder': 'SALARIS',
  'employee.self+salaris': 'SALARIS',
  'loonstrook.verantwoordelijken': 'SALARIS',
  'loonstrook.beheerders': 'SALARIS',
  'loonstrook.verzoek-beoordelaars': 'SALARIS',
  'loonstrook.vrijgave-bewakers': 'SALARIS',
  // batch-68 controle (FO-342 §7)
  'payroll.approvers': 'SALARIS',
  'abonnementen.beheer': 'SALARIS',
  // Beheer, materieel en systeem
  'voorraad.beheer': 'ADMIN',
  'voorraad.minimumbeheer': 'ADMIN',
  // batch-69 magazijn-cijfers (FO-520) - tweede goedkeuring tellingcorrecties.
  'voorraad.goedkeurders': 'ADMIN',
  'uitrusting.verwerkers': 'ADMIN',
  'uitrusting.picklijst': 'ADMIN',
  'uitrusting.besteller': 'ADMIN',
  'uitrusting.levering.beoordelaar': 'ADMIN',
  'voertuigen.beheer': 'ADMIN',
  'voertuigen.beheer+kostprijs': 'ADMIN',
  'skills.beoordelaars': 'HR',
  // batch-71 gesprek-secties (FO-536, FO-537).
  'ontwikkeldoel.eigenaar': 'HR',
  'gesprekcontrole.houders': 'ADMIN',
  // batch-71 verplichte-certificaten (FO-541)
  'verplichte-certificaten.hr+tl': 'HR',
  // batch-70 voertuig-meldingen: wagenpark of salaris per kanaal; een lege groep -> de beheerders.
  'voertuig-melding.doelgroep': 'ADMIN',
  // batch-71 workflow-sjablonen: de lijsten op WorkflowInstelling; een lege groep -> de beheerders.
  'workflow.doelgroep': 'ADMIN',
  'schade.bestuurder': 'ADMIN',
  facilities: 'ADMIN',
  'kluis.eigenaar': 'ADMIN',
  'kluis.vrijgave.beoordelaars': 'ADMIN',
  'kluis.vrijgave.betrokkenen': 'ADMIN',
  'mail.afzender-of-admin': 'ADMIN',
  'all-admins': 'ADMIN',
  // batch-71 kiosk-magazijn
  'kiosk.beheerders': 'ADMIN',
  'reparatie.goedkeurders': 'ADMIN',
  // Management en acquisitie
  directie: 'MANAGER',
  'acquisitie.checkin-verantwoordelijke': 'MANAGER',
  'acquisitie.vervolg-eigenaar': 'MANAGER',
  'lead.owner': 'MANAGER',
  manager: 'MANAGER',
}

/** De STANDAARD-eigenaar per module (`NotificatieEvent.module`), als de resolver niets zegt. */
export const EIGENAAR_ROL_PER_MODULE: Readonly<Record<string, NotificatieEigenaarRol>> = {
  // HR en medewerkers
  verlof: 'HR',
  verzuim: 'HR',
  hrm: 'HR',
  medewerkers: 'HR',
  onboarding: 'HR',
  'offboarding-workflow': 'HR',
  // batch-71 offboarding-inname (FO-535 §7)
  'offboarding-salaris': 'HR',
  'offboarding-inname-conflict': 'HR',
  recruitment: 'HR',
  opleidingen: 'HR',
  gesprekken: 'HR',
  'hr-gesprekken': 'HR',
  gespreksverslagen: 'HR',
  dossier: 'HR',
  vertrouwenspersoon: 'HR',
  werkgeversverklaring: 'HR',
  'informatie-verzoeken': 'HR',
  'contract-mijlpalen': 'HR',
  mutatieverzoeken: 'HR',
  ideeenbox: 'HR',
  boetes: 'HR',
  'contract-verlenging': 'HR',
  'contract-einde-offboarding': 'HR',
  veiligheid: 'HR',
  ongevallen: 'HR',
  formulieren: 'HR',
  communicatie: 'HR',
  certificaten: 'HR',
  cursusplanning: 'HR',
  campagnes: 'HR',
  'hr-besluiten': 'HR',
  // Planning en uitvoering
  planning: 'PLANNER',
  werkbonnen: 'PLANNER',
  uren: 'PLANNER',
  weekstaten: 'PLANNER',
  reisuren: 'PLANNER',
  reisafstanden: 'PLANNER',
  reistijd: 'PLANNER',
  consignatie: 'PLANNER',
  'operationele-taken': 'PLANNER',
  taken: 'PLANNER',
  klantenportaal: 'PLANNER',
  klantverzoeken: 'PLANNER',
  contactverzoeken: 'PLANNER',
  'relatie-onboarding': 'PLANNER',
  relaties: 'PLANNER',
  crm: 'PLANNER',
  // Financieel (administratie)
  facturen: 'SALARIS',
  factuurreeks: 'SALARIS',
  inkoop: 'SALARIS',
  'inkoop-budget': 'SALARIS',
  'inkoop-match': 'SALARIS',
  'inkoop-ontvangst-zonder-factuur': 'SALARIS',
  'interne-facturen': 'SALARIS',
  'interne-geldstroom': 'SALARIS',
  'kostprijs-werkelijk': 'SALARIS',
  'garage-factuur-controle': 'SALARIS',
  besteladvies: 'SALARIS',
  'besteladvies-minimum': 'SALARIS',
  'besteladvies-levering': 'SALARIS',
  'besteladvies-bestelbericht': 'SALARIS',
  declaraties: 'SALARIS',
  salaris: 'SALARIS',
  payroll: 'SALARIS',
  loonstroken: 'SALARIS',
  keuzebudget: 'SALARIS',
  abonnementen: 'SALARIS',
  finqle: 'SALARIS',
  // Beheer, materieel en systeem
  materieel: 'ADMIN',
  middelen: 'ADMIN',
  voorraad: 'ADMIN',
  uitrusting: 'ADMIN',
  bruikleen: 'ADMIN',
  karren: 'ADMIN',
  telefonie: 'ADMIN',
  'voertuig-afspraak': 'ADMIN',
  'voertuig-afspraak-te-plannen': 'ADMIN',
  'voertuig-afspraak-nagekomen': 'ADMIN',
  'voertuig-onderhoud-reminder': 'ADMIN',
  'materieel-terugroep-voorstel': 'ADMIN',
  'materieel-terugroep-heropend': 'ADMIN',
  kluis: 'ADMIN',
  documenten: 'ADMIN',
  mail: 'ADMIN',
  integraties: 'ADMIN',
  import: 'ADMIN',
  systeem: 'ADMIN',
  'notification-engine': 'ADMIN',
  notificaties: 'ADMIN',
  // Management en acquisitie
  acquisitie: 'MANAGER',
  targeting: 'MANAGER',
}

/** Onbekende resolver en onbekende module: beheer. */
export const EIGENAAR_ROL_DEFAULT: NotificatieEigenaarRol = 'ADMIN'

/**
 * PUUR - de AFGELEIDE standaard-eigenaar van een meldingssoort, uit zijn
 * doelgroep-resolver en module.
 *
 * De eigenlijke standaard staat per event en per regel expliciet in de
 * seed-kaart (`packages/db/seed/notificatie-eigenaren.ts`, afgedwongen door een
 * drift-test). Deze functie is daar de achtervang van:
 *  - voor de LEVERINGSLAAG: een soort die niet in de kaart staat (een event uit
 *    een batch-seed, een door beheer aangemaakte signalering) heeft toch een
 *    eigenaar - een melding valt nooit stil weg omdat er een regel ontbreekt;
 *  - voor de DRIFT-TEST: de foutmelding drukt per ontbrekende key de regel af
 *    die in de seed-kaart hoort, met deze rol als voorstel.
 */
export function standaardEigenaarRol(input: {
  resolver?: string | null
  module?: string | null
}): NotificatieEigenaarRol {
  if (input.resolver) {
    const viaResolver = EIGENAAR_ROL_PER_RESOLVER[input.resolver]
    if (viaResolver) return viaResolver
  }
  if (input.module) {
    const viaModule = EIGENAAR_ROL_PER_MODULE[input.module]
    if (viaModule) return viaModule
  }
  return EIGENAAR_ROL_DEFAULT
}

/** De eigenaar zoals het scherm hem toont. */
export interface NotificatieEigenaarDTO {
  /** Symbolische eigenaar (seed-default of beheerderskeuze). */
  rol: NotificatieEigenaarRol | null
  /** Expliciet ingestelde eigenaar-personen; winnen van de rol. */
  userIds: string[]
  /**
   * Dezelfde personen met naam en activiteit (`actief: false` = ontvangt niet
   * meer). Zo kan het scherm een uitgediende eigenaar tonen en eruit laten halen
   * in plaats van hem als lege keuze stil te wissen.
   */
  personen: NotificatieOntvangerDTO[]
  /** Wie er nu als eigenaar zou ontvangen (personen, of de leden van de rol). Afgekapt. */
  ontvangers: NotificatieOntvangerDTO[]
  ontvangersTotaal: number
  ontvangersAfgekapt: boolean
  ingesteldDoorNaam: string | null
  ingesteldOp: string | null
  /**
   * True = niet door beheer ingesteld: dit is de standaard-eigenaar uit de
   * seed-kaart (`packages/db/seed/notificatie-eigenaren.ts`), of voor een soort
   * die daar niet in staat de afgeleide rol (`standaardEigenaarRol`). False = een
   * beheerder heeft personen of een andere rol gekozen.
   */
  standaard: boolean
}

/** Eigenaar zetten. Leeg (geen rol, geen personen) is niet toegestaan: een soort heeft altijd een eigenaar. */
export const notificatieEigenaarRequestSchema = z
  .object({
    eventKey: z.enum(NOTIFICATIE_EVENT_TYPE_KEY).nullable().default(null),
    ruleCode: z.string().min(1).max(120).nullable().default(null),
    rol: z.enum(NOTIFICATIE_EIGENAAR_ROLLEN).nullable().default(null),
    userIds: z.array(z.string().min(1)).max(50).default([]),
  })
  .superRefine((val, ctx) => {
    const aantal = (val.eventKey ? 1 : 0) + (val.ruleCode ? 1 : 0)
    if (aantal !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eventKey'],
        message: 'Geef precies één van eventKey of ruleCode op',
      })
    }
    if (!val.rol && val.userIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rol'],
        message: 'Kies een eigenaar-rol of minstens één persoon',
      })
    }
  })
export type NotificatieEigenaarRequest = z.infer<typeof notificatieEigenaarRequestSchema>

// ── Escalatie-sleutel ────────────────────────────────────────────────────────
//
// Een escalatie heeft een EIGEN override-sleutel: `<ruleCode>:escalatie`. Die
// past in de bestaande `ruleCode`-kolom van NotificatieOntvangerOverride, dus er
// is geen tweede tabel nodig. Zonder instelling gaat de escalatie naar de
// ontvangers van de melding zelf plus de eigenaar - nooit meer naar een hele rol.

export const ESCALATIE_SLEUTEL_SUFFIX = ':escalatie'

export function escalatieRuleCode(ruleCode: string): string {
  return `${ruleCode}${ESCALATIE_SLEUTEL_SUFFIX}`
}

export function isEscalatieRuleCode(code: string): boolean {
  return code.endsWith(ESCALATIE_SLEUTEL_SUFFIX)
}

/** De regel-code zonder het escalatie-achtervoegsel (identiek voor een gewone code). */
export function basisRuleCode(code: string): string {
  return isEscalatieRuleCode(code) ? code.slice(0, -ESCALATIE_SLEUTEL_SUFFIX.length) : code
}

// ── Simulatie met een echte bron-id ──────────────────────────────────────────

export interface NotificatieSimulatieOntvangerDTO extends NotificatieOntvangerDTO {
  via: NotificatieOntvangerVia
  waarom: string
}

export interface NotificatieSimulatieDTO {
  /**
   * De bron waarvoor gesimuleerd is. `null` = zonder bron: klopt voor een
   * doelgroep die niet aan de melding hangt (een rol of permissiegroep), en
   * levert voor een bron-gebonden doelgroep niemand op (zie `opmerking`).
   */
  bronEntiteitId: string | null
  bron: NotificatieOntvangerBron
  ontvangers: NotificatieSimulatieOntvangerDTO[]
  /** Afgekapt op de eerste N namen; `ontvangersTotaal` is het echte aantal. */
  ontvangersTotaal: number
  ontvangersAfgekapt: boolean
  /** Het beheer-signaal dat de live-resolutie zou geven, of null. */
  signaal: string | null
  /** Uitleg wanneer de simulatie niet volledig kan zijn (bv. resolver vereist caller-extras). */
  opmerking: string | null
  /**
   * batch-67 - de gedeelde functie-inboxen waar de melding (ook) als item landt.
   * Leeg = geen gedeeld item: het rolpostvak staat uit, het event maakt geen
   * postvak-item, of de ontvangers zijn vervangen.
   */
  gedeeldPostvak: NotificatieSimulatiePostvakDTO[]
  /**
   * batch-67 - de soort staat uit. Zolang dat zo is krijgt niemand hem; de
   * ontvangers hierboven zijn wie hem krijgt als hij weer aan staat.
   */
  uitgeschakeld: boolean
}

/** Een gedeelde functie-inbox in de simulatie. */
export interface NotificatieSimulatiePostvakDTO {
  functie: InboxFunctie
  label: string
  /**
   * Geen lid dat de inbox kan openen: alleen de beheerders zien het item (het
   * vangnet). Een teken dat er bij Instellingen - Postvak In een lid hoort.
   */
  zonderBruikbaarLid: boolean
}

/**
 * Een recente melding van een soort, als keuze voor de simulatie ("wie krijgt
 * deze melding voor deze bron"). Het scherm biedt deze als zoekbare lijst aan,
 * zodat een beheerder nooit zelf een id hoeft te typen.
 */
export interface NotificatieBronOptieDTO {
  bronEntiteitId: string
  /** Titel van de laatste melding over deze bron (herkenbaar voor de beheerder). */
  titel: string
  laatsteOp: string
}

// ── Afleveringen-tab ─────────────────────────────────────────────────────────

const ISO_DAG = /^\d{4}-\d{2}-\d{2}$/

/** Bestaat deze `jjjj-mm-dd` in de kalender? (`2026-02-31` haalt de regex wel.) */
function bestaandeIsoDag(waarde: string): boolean {
  const [y, m, d] = waarde.split('-').map(Number)
  const check = new Date(Date.UTC(y, m - 1, d))
  return check.getUTCFullYear() === y && check.getUTCMonth() === m - 1 && check.getUTCDate() === d
}

const isoDag = z
  .string()
  .regex(ISO_DAG, 'Gebruik het formaat jjjj-mm-dd')
  .refine(bestaandeIsoDag, 'Deze datum bestaat niet')

export const notificatieAfleveringenQuerySchema = z
  .object({
    userId: z.string().min(1).optional(),
    eventKey: z.enum(NOTIFICATIE_EVENT_TYPE_KEY).optional(),
    ruleCode: z.string().min(1).max(120).optional(),
    /**
     * Hoe de ontvangers tot stand kwamen (uit het spoor). `EIGENAAR` = de melding
     * had geen gerichte ontvanger en ging naar de eigenaar: de lijst waar beheer
     * iets aan moet doen.
     */
    bron: z.enum(NOTIFICATIE_ONTVANGER_BRONNEN).optional(),
    /**
     * Het leverpad (uit het spoor). Samen met `ruleCode` onderscheidt dit de
     * melding van een regel (`REGEL`) van zijn escalatie (`ESCALATIE`): die delen
     * dezelfde regel-code.
     */
    pad: z.enum(NOTIFICATIE_LEVERPADEN).optional(),
    vanaf: isoDag.optional(),
    totEnMet: isoDag.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
  })
  .superRefine((q, ctx) => {
    if (q.vanaf && q.totEnMet && q.totEnMet < q.vanaf) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['totEnMet'],
        message: 'De einddatum ligt voor de begindatum',
      })
    }
  })
export type NotificatieAfleveringenQuery = z.infer<typeof notificatieAfleveringenQuerySchema>

export interface NotificatieAfleveringRijDTO {
  notificatieId: string
  userId: string
  userNaam: string
  userEmail: string
  eventKey: string | null
  titel: string
  createdAt: string
  gelezenOp: string | null
  /** De ontvanger heeft de melding uit zijn bel gewist; afgeleverd is hij wel. */
  verwijderdOp: string | null
  kanalen: Array<{ kanaal: NotificatieKanaalType; status: NotificatieAfleveringStatus }>
  ontvangerBron: NotificatieOntvangerBronDTO | null
  /** De "Waarom"-zin, server-side opgebouwd met het doelgroep-label. */
  waarom: string | null
}

// ── "Ik hoor dit niet te krijgen" ────────────────────────────────────────────

/**
 * Wat de actie deed (zie `apps/api/src/lib/notificatie-events/niet-voor-mij.ts`):
 *  - EXCLUDE: een EXCLUDE-rij in `NotificatieVoorkeurOverride` (een melding die
 *    je als lid van een groep kreeg);
 *  - MUTE: een persoonlijke demping op de signalering-regel (een regel-melding
 *    met een eigen template, of een escalatie);
 *  - ALLEEN_SIGNAAL: er is voor de ontvanger niets uitgezet, beheer is wel
 *    geseind. Bij een verplichte of kritieke melding, een melding die aan de
 *    persoon zelf is gericht, of een ontvanger die beheer met naam of via een
 *    gekozen rol heeft ingesteld: daar zou een uitsluiting niets doen, al je
 *    eigen meldingen dempen of een besluit van beheer stil terugdraaien;
 *  - GEEN_WIJZIGING: er valt niets uit te zetten en niets te melden (een
 *    testmelding, het beheer-signaal zelf, het vangnet naar de beheerders).
 */
export const NIET_VOOR_MIJ_UITKOMSTEN = [
  'EXCLUDE',
  'MUTE',
  'ALLEEN_SIGNAAL',
  'GEEN_WIJZIGING',
] as const
export type NietVoorMijUitkomst = (typeof NIET_VOOR_MIJ_UITKOMSTEN)[number]

export interface NietVoorMijResultaatDTO {
  uitkomst: NietVoorMijUitkomst
  melding: string
}

export interface NotificatieOntvangerAuditDTO {
  id: string
  soortId: string
  action: string
  changes: unknown
  userId: string
  userNaam: string | null
  createdAt: string
}

// ─── Request-schema's ────────────────────────────────────────────────────────

export const NOTIFICATIE_ONTVANGER_ROLLEN = [
  'MONTEUR',
  'TEAMLEIDER',
  'PLANNER',
  'HR',
  'SALARIS',
  'MANAGER',
  'ADMIN',
] as const
export type NotificatieOntvangerRol = (typeof NOTIFICATIE_ONTVANGER_ROLLEN)[number]

export const NOTIFICATIE_ONTVANGER_ROL_LABELS: Record<NotificatieOntvangerRol, string> = {
  MONTEUR: 'Monteurs',
  TEAMLEIDER: 'Teamleiders',
  PLANNER: 'Planners',
  HR: 'HR',
  SALARIS: 'Salarisadministratie',
  MANAGER: 'Management',
  ADMIN: 'Beheer (ADMIN)',
}

/**
 * Override zetten of herstellen.
 *
 * `bevestigNiemand` is de expliciete bevestigingsvraag uit spec §3.5: een lege
 * selectie herstelt de standaard-doelgroep, tenzij de beheerder bevestigt dat
 * niemand deze melding hoort te krijgen. Zonder die bevestiging kan een
 * override dus nooit stil naar nul ontvangers zakken.
 *
 * De ACCOUNTANT-rol ontbreekt bewust in `NOTIFICATIE_ONTVANGER_ROLLEN`: de
 * externe accountant krijgt per FO-404 §7 nooit notificaties, en de
 * leveringslaag filtert hem hoe dan ook weg.
 */
export const notificatieOntvangerOverrideRequestSchema = z.object({
  eventKey: z.enum(NOTIFICATIE_EVENT_TYPE_KEY).nullable().default(null),
  ruleCode: z.string().min(1).max(120).nullable().default(null),
  userIds: z.array(z.string().min(1)).max(200).default([]),
  rollen: z.array(z.enum(NOTIFICATIE_ONTVANGER_ROLLEN)).max(NOTIFICATIE_ONTVANGER_ROLLEN.length).default([]),
  bevestigNiemand: z.boolean().default(false),
}).superRefine((val, ctx) => {
  const aantal = (val.eventKey ? 1 : 0) + (val.ruleCode ? 1 : 0)
  if (aantal !== 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['eventKey'],
      message: 'Geef precies één van eventKey of ruleCode op',
    })
  }
})
export type NotificatieOntvangerOverrideRequest = z.infer<
  typeof notificatieOntvangerOverrideRequestSchema
>
